import { useEffect, useState } from "react";
import AppShell from "../../components/app/AppShell";
import AppEmptyState from "../../components/app/AppEmptyState";
import { FullPageLoading, InlineSpinner } from "../../components/Loading";
import {
  inviteFamilyMember,
  listFamily,
  listProfiles,
  removeFamilyMember,
  updateFamilyRole,
  type FamilyMember,
  type FamilyRole,
} from "../../lib/api";

// /app/family — invite & manage family members. Backed by the
// /me/family/* endpoints. Roles map to the three pills below.

const ROLE_LABELS: Record<FamilyRole, string> = {
  vollzugriff: "Vollzugriff",
  lesen_hoeren: "Lesen + Hören",
  lesen: "Lesen",
};

const ROLE_PILLS: FamilyRole[] = ["lesen", "lesen_hoeren", "vollzugriff"];
const MAX_MEMBERS = 5;

export default function AppFamily() {
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState<string | null>(null);
  const [storyteller, setStoryteller] = useState<{ first: string; last: string }>({
    first: "",
    last: "",
  });
  const [ordererEmail, setOrdererEmail] = useState("");
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Invite form
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<FamilyRole>("lesen_hoeren");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);

  async function reload(p: string) {
    try {
      const r = await listFamily(p);
      setMembers(r.members || []);
      setStoryteller({
        first: r.storyteller?.first_name || "",
        last: r.storyteller?.last_name || "",
      });
      setOrdererEmail(r.orderer_email || "");
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Konnte Familie nicht laden.");
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0];
        if (!p) {
          setLoading(false);
          return;
        }
        setPhone(p.phone);
        await reload(p.phone);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <FullPageLoading />;

  if (!phone) {
    return (
      <AppShell
        eyebrow="Familie"
        title={
          <>
            Mit <em>der Familie</em> teilen
          </>
        }
      >
        <AppEmptyState
          icon="family"
          title="Noch kein Erzähler-Profil verknüpft."
          body="Familienmitglieder werden an ein Erzähler-Profil eingeladen."
        />
      </AppShell>
    );
  }

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!phone || !email.trim()) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await inviteFamilyMember(phone, email.trim().toLowerCase(), role);
      setSuccess(
        r.email_sent
          ? `Einladung an ${r.email} verschickt.`
          : `${r.email} wurde hinzugefügt — E-Mail-Versand wird gerade aufgesetzt.`,
      );
      setEmail("");
      await reload(phone);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Konnte nicht einladen.");
    } finally {
      setSending(false);
    }
  }

  async function onRoleChange(member: FamilyMember, next: FamilyRole) {
    if (!phone || member.role === next) return;
    setBusyRow(member.email);
    try {
      await updateFamilyRole(phone, member.email, next);
      await reload(phone);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Konnte Rolle nicht ändern.");
    } finally {
      setBusyRow(null);
    }
  }

  async function onRemove(member: FamilyMember) {
    if (!phone) return;
    const ok = window.confirm(
      `${member.name || member.email} aus der Familie entfernen?`,
    );
    if (!ok) return;
    setBusyRow(member.email);
    try {
      await removeFamilyMember(phone, member.email);
      await reload(phone);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Konnte nicht entfernen.");
    } finally {
      setBusyRow(null);
    }
  }

  const storytellerName =
    [storyteller.first, storyteller.last].filter(Boolean).join(" ") || "Erzähler:in";
  const limitReached = members.length >= MAX_MEMBERS;

  return (
    <AppShell
      eyebrow="Mein Buch"
      title={
        <>
          Wer hört <em>mit</em>?
        </>
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 15,
            lineHeight: 1.5,
            color: "var(--ink-soft)",
            margin: "0 0 22px",
          }}
        >
          Familienmitglieder können Kapitel lesen, Anrufe nachhören und Fotos
          hinzufügen — je nach Rolle.
        </p>

        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(184,52,40,0.08)",
              border: "1px solid rgba(184,52,40,0.30)",
              borderRadius: 8,
              padding: "10px 14px",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--error)",
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <div className="t-caps" style={{ marginBottom: 8 }}>
          Erzählerin
        </div>
        <StorytellerCard name={storytellerName} />

        <div style={{ height: 18 }} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 8,
          }}
        >
          <div className="t-caps">Familie</div>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-faint)",
            }}
          >
            {members.length} von {MAX_MEMBERS} Zugängen
          </div>
        </div>

        {/* Orderer row (always present, can't be removed) */}
        {ordererEmail && (
          <FamilyRow
            email={ordererEmail}
            name="Sie selbst"
            roleLabel="Vollzugriff · Auftraggeber/in"
            isOwner
          />
        )}

        {members.map((m) => (
          <FamilyRow
            key={m.email}
            email={m.email}
            name={m.name || m.email}
            roleLabel={ROLE_LABELS[m.role]}
            onRoleClick={(r) => onRoleChange(m, r)}
            currentRole={m.role}
            busy={busyRow === m.email}
            onRemove={() => onRemove(m)}
          />
        ))}

        {members.length === 0 && (
          <div
            style={{
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-faint)",
              padding: "10px 0 0",
            }}
          >
            Noch niemand eingeladen.
          </div>
        )}

        {/* Invite card */}
        <form
          onSubmit={onInvite}
          style={{
            marginTop: 20,
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 14,
            padding: "18px 18px",
            opacity: limitReached ? 0.55 : 1,
          }}
        >
          <div className="t-caps" style={{ marginBottom: 10 }}>
            Einladen
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "var(--paper-100)",
              border: "1px solid rgba(43,26,15,0.12)",
              borderRadius: 10,
              padding: "10px 14px",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--ink-faint)"
              strokeWidth="1.6"
              aria-hidden
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
            <input
              type="email"
              required
              placeholder="email@familie.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={limitReached || sending}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontFamily: "var(--f-body)",
                fontSize: 15,
                color: "var(--ink)",
              }}
            />
          </label>
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            {ROLE_PILLS.map((r) => {
              const selected = role === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  disabled={limitReached || sending}
                  style={{
                    flex: 1,
                    textAlign: "center",
                    padding: "8px 8px",
                    background: selected ? "var(--oxblood)" : "transparent",
                    color: selected ? "var(--paper-50)" : "var(--ink-soft)",
                    border: selected
                      ? "1.5px solid var(--oxblood)"
                      : "1px solid rgba(43,26,15,0.14)",
                    borderRadius: 8,
                    fontFamily: "var(--f-ui)",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: limitReached ? "not-allowed" : "pointer",
                  }}
                >
                  {ROLE_LABELS[r]}
                </button>
              );
            })}
          </div>
          <button
            type="submit"
            className="btn-primary"
            disabled={limitReached || sending || !email.trim()}
            style={{
              width: "100%",
              boxSizing: "border-box",
              marginTop: 14,
            }}
          >
            {sending ? <InlineSpinner /> : "Einladen"}
          </button>
          {success && (
            <div
              role="status"
              style={{
                marginTop: 12,
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--sage)",
              }}
            >
              {success}
            </div>
          )}
          {limitReached && (
            <div
              style={{
                marginTop: 12,
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 12,
                color: "var(--ink-faint)",
              }}
            >
              Sie haben das Limit von {MAX_MEMBERS} Familien-Zugängen erreicht.
              Entfernen Sie ein Mitglied, um Platz zu schaffen.
            </div>
          )}
        </form>
      </div>
    </AppShell>
  );
}

function StorytellerCard({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 16px",
        background: "var(--paper-50)",
        border: "1.5px solid var(--ochre)",
        borderRadius: 12,
        marginBottom: 8,
      }}
    >
      <div
        className="oval-frame"
        style={{
          width: 40,
          height: 40,
          background: "rgba(168,122,58,0.20)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontFamily: "var(--f-display)",
          fontStyle: "italic",
          fontSize: 16,
          color: "var(--ochre)",
        }}
      >
        {initials || "—"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 500,
            fontSize: 15,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--ink-faint)",
            marginTop: 1,
          }}
        >
          erzählt diese Geschichte
        </div>
      </div>
    </div>
  );
}

function FamilyRow({
  email,
  name,
  roleLabel,
  isOwner,
  currentRole,
  onRoleClick,
  onRemove,
  busy,
}: {
  email: string;
  name: string;
  roleLabel: string;
  isOwner?: boolean;
  currentRole?: FamilyRole;
  onRoleClick?: (r: FamilyRole) => void;
  onRemove?: () => void;
  busy?: boolean;
}) {
  const initials = name
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() ||
    email.slice(0, 2).toUpperCase();
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      style={{
        background: "var(--paper-50)",
        border: "1px solid rgba(43,26,15,0.10)",
        borderRadius: 12,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 16px",
        }}
      >
        <div
          className="oval-frame"
          style={{
            width: 40,
            height: 40,
            background: "var(--paper-200)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontFamily: "var(--f-display)",
            fontStyle: "italic",
            fontSize: 16,
            color: "var(--oxblood)",
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 500,
              fontSize: 15,
              color: "var(--ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
            {isOwner && (
              <span
                style={{
                  fontFamily: "var(--f-ui)",
                  fontSize: 10,
                  fontWeight: 600,
                  background: "rgba(107,32,24,0.10)",
                  color: "var(--oxblood)",
                  padding: "1px 6px",
                  borderRadius: 3,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  marginLeft: 6,
                }}
              >
                Sie
              </span>
            )}
          </div>
          <div
            style={{
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-faint)",
              marginTop: 1,
            }}
          >
            {roleLabel}
          </div>
        </div>
        {!isOwner && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            disabled={busy}
            style={{
              background: "transparent",
              border: "1px solid rgba(43,26,15,0.14)",
              borderRadius: 999,
              padding: "5px 10px",
              fontFamily: "var(--f-ui)",
              fontSize: 11,
              fontWeight: 500,
              color: "var(--ink-soft)",
              cursor: "pointer",
            }}
          >
            {busy ? "…" : expanded ? "Schließen" : "Verwalten"}
          </button>
        )}
      </div>
      {expanded && !isOwner && currentRole && onRoleClick && (
        <div
          style={{
            borderTop: "1px dashed rgba(43,26,15,0.14)",
            padding: "12px 16px",
          }}
        >
          <div
            className="t-caps"
            style={{ marginBottom: 8, color: "var(--ink-faint)" }}
          >
            Rolle ändern
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {ROLE_PILLS.map((r) => {
              const selected = currentRole === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => onRoleClick(r)}
                  disabled={busy}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    background: selected ? "var(--oxblood)" : "transparent",
                    color: selected ? "var(--paper-50)" : "var(--ink-soft)",
                    border: selected
                      ? "1.5px solid var(--oxblood)"
                      : "1px solid rgba(43,26,15,0.14)",
                    borderRadius: 8,
                    fontFamily: "var(--f-ui)",
                    fontSize: 11,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {ROLE_LABELS[r]}
                </button>
              );
            })}
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              className="btn-secondary"
              style={{
                width: "100%",
                fontSize: 12,
                color: "var(--oxblood)",
                borderColor: "var(--oxblood)",
              }}
            >
              Zugang entfernen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
